import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock } from 'lucide-react';
import { useClientLock } from '@/features/auth/contexts/ClientLockContext';
import { useLanguage } from '@/shared/contexts/LanguageContext';

/**
 * Global, controlled by ClientLockContext. Mount once near the app root.
 * Asks for the practitioner PIN to authorise a pending sensitive action
 * (or to fully release the client lock).
 */
export function PinPromptModal() {
  const { _modal } = useClientLock();
  const { t } = useLanguage();
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (_modal.open) {
      setPin('');
      setError(false);
    }
  }, [_modal.open]);

  const handleSubmit = () => {
    const ok = _modal.submit(pin);
    if (!ok) {
      setError(true);
      setPin('');
    }
  };

  return (
    <AnimatePresence>
      {_modal.open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={_modal.close}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="card-elevated p-8 max-w-sm w-full text-center space-y-5"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="text-primary" size={28} />
            </div>
            <h3 className="text-xl font-bold font-display">{t('pin.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('pin.enter')}</p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                setError(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              className="w-full text-center text-2xl tracking-[0.8em] py-3 bg-secondary border-2 border-border rounded-xl focus:border-primary focus:outline-none"
              autoFocus
            />
            {error && (
              <p className="text-destructive text-sm font-medium">{t('pin.incorrect')}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={_modal.close}
                className="flex-1 py-3 bg-secondary text-secondary-foreground rounded-xl font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold"
              >
                {t('common.confirm')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
